import React, { useMemo, Fragment } from "react";
import { StickyContainer } from "react-sticky";
import ItineraryDay from "./ItineraryDay";
import ItineraryPlaceStartCard from "./ItineraryPlaceStartCard";
import ItineraryPlaceEndCard from "./ItineraryPlaceEndCard";
import ItineraryStopCard from "./ItineraryStopCard";
import { useTranslate } from "../../translate";
import { AnimatePresence } from "framer-motion";
import Fade from "../animations/Fade";
import Tween from "./Tween";
import GenericTween from "./GenericTween";
import ItineraryEndTween from "./ItineraryEndTween";
import ItinerarySuggestion from "./ItinerarySuggestion";
import ItineraryFooter from "./ItineraryFooter";

const fadeTransition = {
    duration: 0.25,
    ease: "linear",
};

function ItineraryStops(props) {
    const {
        items,
        itemsGroupByDay,
        stopsById,
        distancesByStopDayNumber,
        activeStopId,
        className,
        dayTopOffset,
        activeDayId,
        setIsOpenItinerarySwitcher,
        showItinerarySwitcher,
        inLauncherModal,
        useTwoColumnLayout,
        useOldLayout,
        daysById,
        activeSuggestionId,
        itineraryMetadata,
        setIsEmailModalVisible,
        showToast,
    } = props;
    const translate = useTranslate();
    const isActiveStopOvernight = stopsById[activeStopId]?.stopType?.label?.toLowerCase() === "overnight";

    return useMemo(() => {
        if (!items?.length) {
            return null;
        }
        const activeDay = daysById[activeDayId];
        return (
            <div className={`trpr__primary-layout-itinerary-items-wrapper ${className}`}>
                {!useOldLayout && !useTwoColumnLayout && (
                    <div style={{ position: "fixed", top: 0, left: 0, width: "100%", zIndex: 6 }}>
                        <AnimatePresence>
                            {activeDay && (
                                <Fade transition={fadeTransition}>
                                    <ItineraryDay
                                        dayNumberFixed={activeDay.dayNumberFixed}
                                        dayNumber={activeDay.dayNumber}
                                        translate={translate}
                                        distance={distancesByStopDayNumber[activeDay.dayNumber]}
                                        topOffset={0}
                                        isActiveStopOvernight={isActiveStopOvernight}
                                        activeDayId={activeDayId}
                                        setIsOpenItinerarySwitcher={setIsOpenItinerarySwitcher}
                                        showItinerarySwitcher={showItinerarySwitcher}
                                        dayId={activeDayId}
                                        inLauncherModal={inLauncherModal}
                                        withSticky={false}
                                        isStickyOverride={true}
                                    />
                                </Fade>
                            )}
                        </AnimatePresence>
                    </div>
                )}
                {/* <div className="trpr__card-arrow"></div> */}
                {itemsGroupByDay.map((stopGroup, stopGroupIndex) => {
                    const addPTClass = stopGroupIndex === 0;
                    const addPBClass =
                        ["stop", "place_overnight", "place_end"].includes(stopGroup[stopGroup.length - 1].type) &&
                        stopGroupIndex !== itemsGroupByDay.length - 1;
                    const groupClassName = `${addPBClass ? "with-pb" : ""} ${addPTClass ? "with-pt" : ""}`;

                    const isLastGroup = stopGroupIndex === itemsGroupByDay.length - 1;
                    return (
                        <StickyContainer className={`trpr__card-group ${groupClassName}`} key={stopGroupIndex}>
                            {stopGroup.map((stop, index) => {
                                if (!stop.id) {
                                    return null;
                                } else if (stop.type === "stop") {
                                    const isLastStop = index === stopGroup.length - 1;
                                    const className = `js__stop-card ${
                                        stop?.stopType?.label?.toLowerCase() === "overnight" || isLastStop
                                            ? "js__stop-card-overnight"
                                            : ""
                                    }`;
                                    return (
                                        <Fragment key={`${stop.id}-stop`}>
                                            <ItineraryStopCard
                                                order={stop.stopNumber}
                                                coords={stopsById[stop.id].coords}
                                                stop={stop}
                                                isActive={stop.id === activeStopId}
                                                className={className}
                                                data-stop-number={stop.stopNumber}
                                                data-stop-id={stop.id}
                                            />
                                        </Fragment>
                                    );
                                } else if (stop.type === "day") {
                                    const key = `${stop.id}-day`;
                                    return (
                                        <div
                                            key={key}
                                            className="trpr__group-header-sticky-container js__itinerary-day"
                                            data-day-id={key}
                                        >
                                            {/* Hide first itinerary day on mobile (only in PlannerContainer) */}
                                            {(useOldLayout || useTwoColumnLayout || stop.dayNumber !== 1) && (
                                                <ItineraryDay
                                                    dayNumberFixed={stop.dayNumberFixed}
                                                    dayNumber={stop.dayNumber}
                                                    translate={translate}
                                                    distance={distancesByStopDayNumber[stop.dayNumber]}
                                                    topOffset={dayTopOffset}
                                                    isActiveStopOvernight={isActiveStopOvernight}
                                                    activeDayId={activeDayId}
                                                    setIsOpenItinerarySwitcher={setIsOpenItinerarySwitcher}
                                                    showItinerarySwitcher={showItinerarySwitcher}
                                                    dayId={key}
                                                    inLauncherModal={inLauncherModal}
                                                    withSticky={useOldLayout || useTwoColumnLayout}
                                                />
                                            )}
                                        </div>
                                    );
                                } else if (stop.type === "distance") {
                                    return (
                                        <GenericTween
                                            key={`${stop.id}-distance`}
                                            className="trpr-itinerary-tween"
                                            icon="road"
                                            singleLine={true}
                                        >
                                            {`${stop.distToNext.toFixed(1)} km${
                                                stop.timeToNext ? ` (${stop.timeToNext})` : ""
                                            } ${translate("to next stop")}`}
                                        </GenericTween>
                                    );
                                } else if (stop.type === "tween") {
                                    return (
                                        <Tween
                                            className="trpr-itinerary-tween"
                                            key={`${stop.id}-tween`}
                                            data={stop}
                                        ></Tween>
                                    );
                                } else if (stop.type === "place_start") {
                                    return (
                                        <ItineraryPlaceStartCard
                                            key={`${stop.id}-place_start`}
                                            data-place-start-id={stop.id}
                                            title={translate(stop, "name")}
                                            description={translate(stop, "description")}
                                            stopsCount={stop.stopsUntilPlaceEndCount}
                                            stop={stop}
                                            className="trpr-itinerary-place-start-card-container js__place-start-card"
                                        />
                                    );
                                } else if (stop.type === "place_end") {
                                    return <ItineraryPlaceEndCard key={`${stop.id}-place_end`} item={stop} />;
                                } else if (stop.type === "place_overnight") {
                                    return (
                                        <ItinerarySuggestion
                                            className="trpr-itinerary-suggestion-card-container js__suggestion-card"
                                            data-stop-id={stop.id}
                                            key={`${stop.id}-suggestion`}
                                            item={stop}
                                            isActive={stop.id === activeSuggestionId}
                                        ></ItinerarySuggestion>
                                    );
                                }
                                return null;
                            })}
                            {isLastGroup && (
                                <Fragment>
                                    <ItineraryEndTween
                                        title="End of itinerary"
                                        description={itineraryMetadata && itineraryMetadata.departureDay}
                                        className="trpr-itinerary-tween"
                                    ></ItineraryEndTween>
                                    <ItineraryFooter
                                        setIsEmailModalVisible={setIsEmailModalVisible}
                                        showToast={showToast}
                                        itineraryMetadata={itineraryMetadata}
                                        className="js__itinerary-footer"
                                    />
                                </Fragment>
                            )}
                        </StickyContainer>
                    );
                })}
            </div>
        );
    }, [
        itineraryMetadata,
        setIsEmailModalVisible,
        showToast,
        activeSuggestionId,
        activeDayId,
        dayTopOffset,
        isActiveStopOvernight,
        className,
        activeStopId,
        items,
        itemsGroupByDay,
        stopsById,
        translate,
        distancesByStopDayNumber,
        inLauncherModal,
        setIsOpenItinerarySwitcher,
        showItinerarySwitcher,
        useTwoColumnLayout,
        useOldLayout,
        daysById,
    ]);
}

export default ItineraryStops;
